import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import type { FunctionComponent, SVGProps } from "react";
import {
    faArrowDown,
    faArrowUp,
    faBuilding,
    faClipboardList,
    faClock,
    faFlag,
    faGear,
    faGlobe,
    faGlobeAmericas,
    faLayerGroup,
    faLocationDot,
    faRecycle,
    faSitemap,
    faTrash,
    faUsers,
    faGraduationCap,
} from "@fortawesome/free-solid-svg-icons";
import { faExcavatorSolid } from "../icons/faExcavatorSolid";
import FroomNavCustomersHubSvg from "../icons/hub/FroomNavCustomersHub.svg?react";
import FroomNavVehicleHubSvg from "../icons/hub/FroomNavVehicleHub.svg?react";
import FroomNavHauliersHubSvg from "../icons/hub/FroomNavHauliersHub.svg?react";
import FroomNavTippersHubSvg from "../icons/hub/FroomNavTippersHub.svg?react";
import FroomNavProcessorsHubSvg from "../icons/hub/FroomNavProcessorsHub.svg?react";
import FroomNavEwcCodesHubSvg from "../icons/hub/FroomNavEwcCodesHub.svg?react";
import FroomNavPhysicalStatesHubSvg from "../icons/hub/FroomNavPhysicalStatesHub.svg?react";
import FroomNavPreTreatmentHubSvg from "../icons/hub/FroomNavPreTreatmentHub.svg?react";
import FroomNavSourceActivityHubSvg from "../icons/hub/FroomNavSourceActivityHub.svg?react";
import { appRoutes } from "../routes/appRoutes";
import { referenceAdminListPaths, referenceAdminRoutePaths } from "../routes/referenceAdminRoutes";
import { ListPageTitles, SectionLabels } from "../utils/navigation/pageTitles";
import { ADMIN_ROLES } from "../utils/helpers/role";

export const HUB_WELCOME_DESCRIPTION = "Welcome. Use the sidebar or the shortcuts below.";

/** SVGR default export: inline SVG with `fill="currentColor"` so hub styling (e.g. `text-primary`) applies. */
export type HubInlineSvgComponent = FunctionComponent<SVGProps<SVGSVGElement>>;

/** Hub card leading art: Font Awesome, inline SVG (SVGR), or a public URL under `public/`. */
export type HubCardGraphic =
    | { kind: "fontawesome"; icon: IconDefinition }
    | { kind: "inlineSvg"; Svg: HubInlineSvgComponent }
    | { kind: "svg"; src: string; alt?: string };

export function hubFa(icon: IconDefinition): HubCardGraphic {
    return { kind: "fontawesome", icon };
}

/** Inline SVG from `*.svg?react` (vite-plugin-svgr); use `currentColor` in the asset for theme colour. */
export function hubInlineSvg(Svg: HubInlineSvgComponent): HubCardGraphic {
    return { kind: "inlineSvg", Svg };
}

/** `src` is served from `public/` (Vite/CRA base URL). */
export function hubSvg(src: string, alt?: string): HubCardGraphic {
    return { kind: "svg", src, alt };
}

const dashboardIcon = "/icons/froom-nav-dashboard.svg";
const dashboardIconSelected = "/icons/froom-nav-dashboard-selected.svg";
const settingsIcon = "/icons/froom-nav-settings.svg";
const settingsIconSelected = "/icons/froom-nav-settings-selected.svg";
const operationsIcon = "/icons/froom-nav-operations.svg";
const operationsIconSelected = "/icons/froom-nav-operations-selected.svg";
const movementIcon = "/icons/froom-nav-movement-parties.svg";
const movementIconSelected = "/icons/froom-nav-movement-parties-selected.svg";
const refDataIcon = "/icons/froom-nav-ref-data.svg";
const refDataIconSelected = "/icons/froom-nav-ref-data-selected.svg";

export type SidebarNavItem = {
    name: string;
    path: string;
    icon: string;
    selectedIcon: string;
    roles?: readonly string[];
};

/** Top-level sidebar entries (order preserved). */
export const sidebarNavItems: SidebarNavItem[] = [
    { name: "Dashboard", path: "/dashboard", icon: dashboardIcon, selectedIcon: dashboardIconSelected },
    { name: SectionLabels.operations, path: "/operations", icon: operationsIcon, selectedIcon: operationsIconSelected },
    { name: SectionLabels.movementParties, path: appRoutes.transportParties, icon: movementIcon, selectedIcon: movementIconSelected },
    { name: SectionLabels.referenceData, path: appRoutes.codes, icon: refDataIcon, selectedIcon: refDataIconSelected, roles: [...ADMIN_ROLES] },


    { name: SectionLabels.system, path: "/system", icon: settingsIcon, selectedIcon: settingsIconSelected },
];

export type HubCardDef = {
    title: string;
    to: string;
    description: string;
    icon: HubCardGraphic;
    roles?: readonly string[];
};

export const operationsHubCards: HubCardDef[] = [
    // Branch A: view-only discoverability for all authenticated users; admin actions gated on SitesListPage.
    {
        title: "Sites",
        to: "/sites",
        description: "View regulated yards and site details you can access.",
        icon: hubFa(faExcavatorSolid),
    },
    { title: "Customers", to: "/customers", description: "View and manage customers", icon: hubInlineSvg(FroomNavCustomersHubSvg) },
    { title: "Locations", to: "/locations", description: "View and manage known locations", icon: hubFa(faLocationDot) },
    { title: ListPageTitles.wasteReceived, to: appRoutes.wasteReceived, description: "Record daily waste received for EA returns", icon: hubFa(faArrowDown) },
    { title: ListPageTitles.wasteRemoved, to: appRoutes.wasteRemoved, description: "Record daily waste removed for EA returns", icon: hubFa(faArrowUp) },
    { title: ListPageTitles.wasteReturns, to: appRoutes.wasteReturns, description: "Review period totals before future EA export", icon: hubFa(faClipboardList) },
];

export const movementPartiesHubCards: HubCardDef[] = [
    { title: "Vehicles", to: "/vehicles", description: "View and manage known vehicles", icon: hubInlineSvg(FroomNavVehicleHubSvg) },
    { title: "Hauliers", to: "/hauliers", description: "View and manage hauliers", icon: hubInlineSvg(FroomNavHauliersHubSvg) },
    { title: "Tippers", to: "/tippers", description: "View and manage tippers", icon: hubInlineSvg(FroomNavTippersHubSvg) },
    { title: "Processors", to: "/processors", description: "View and manage processors", icon: hubInlineSvg(FroomNavProcessorsHubSvg) },
];

export const referenceDataHubCards: HubCardDef[] = [
    { title: "Waste Types", to: "/waste-types", description: "View and manage waste types", icon: hubFa(faTrash), roles: ADMIN_ROLES },
    {
        title: ListPageTitles.ewcCodes,
        to: referenceAdminRoutePaths.ewcCodes.list,
        description: "View and manage EWC waste codes",
        icon: hubInlineSvg(FroomNavEwcCodesHubSvg),
        roles: ADMIN_ROLES,
    },
    {
        title: ListPageTitles.disposalRecoveryCodes,
        to: referenceAdminRoutePaths.disposalRecoveryCodes.list,
        description: "View and manage disposal and recovery codes",
        icon: hubFa(faRecycle),
        roles: ADMIN_ROLES,
    },
    {
        title: ListPageTitles.physicalStates,
        to: referenceAdminRoutePaths.physicalStates.list,
        description: "View and manage physical states",
        icon: hubInlineSvg(FroomNavPhysicalStatesHubSvg),
        roles: ADMIN_ROLES,
    },
    {
        title: ListPageTitles.preTreatments,
        to: referenceAdminRoutePaths.preTreatments.list,
        description: "View and manage pre-treatment values",
        icon: hubInlineSvg(FroomNavPreTreatmentHubSvg),
        roles: ADMIN_ROLES,
    },
    {
        title: ListPageTitles.sourceActivities,
        to: referenceAdminRoutePaths.sourceActivities.list,
        description: "View and manage source activity values",
        icon: hubInlineSvg(FroomNavSourceActivityHubSvg),
        roles: ADMIN_ROLES,
    },
    {
        title: ListPageTitles.geography,
        to: referenceAdminRoutePaths.geography.list,
        description: "Area codes (type cannot be changed after saving)",
        icon: hubFa(faGlobeAmericas),
        roles: ADMIN_ROLES,
    },
    {
        title: ListPageTitles.municipalDegradableIndicators,
        to: referenceAdminRoutePaths.municipalDegradables.list,
        description: "Municipal and biodegradable Yes/No reporting indicators",
        icon: hubFa(faFlag),
        roles: ADMIN_ROLES,
    },
    {
        title: ListPageTitles.facilityOrOperations,
        to: referenceAdminRoutePaths.facilityOperations.list,
        description: "View and manage facility or operation types",
        icon: hubFa(faBuilding),
        roles: ADMIN_ROLES,
    },
    {
        title: ListPageTitles.technicalCompetenceSchemes,
        to: referenceAdminRoutePaths.technicalCompetenceSchemes.list,
        description: "View and manage technical competence schemes",
        icon: hubFa(faGraduationCap),
        roles: ADMIN_ROLES,
    },
];

export const adminFoundationHubCards: HubCardDef[] = [
    {
        title: "Sign-in & Invitations",
        to: appRoutes.websiteSettings,
        description: "Sign-in timeout and invitation settings. Uses your saved value when set; otherwise 12 hours. Restart may be needed for changes to take effect.",
        icon: hubFa(faClock),
        roles: [...ADMIN_ROLES],
    },
    {
        title: "Sites",
        to: "/sites",
        description: "View regulated yards and site details you can access.",
        icon: hubFa(faExcavatorSolid),
        roles: [...ADMIN_ROLES],
    },
    {
        title: SectionLabels.referenceData,
        to: appRoutes.codes,
        description: "Regulatory and reporting code settings",
        icon: hubFa(faRecycle),
        roles: [...ADMIN_ROLES],
    },
];

export const systemHubCards: HubCardDef[] = [
    { title: SectionLabels.settings, to: "/settings", description: "View and manage system settings", icon: hubFa(faGear) },
    {
        title: SectionLabels.adminFoundation,
        to: appRoutes.systemSetup,
        description: "Shortcuts: sign-in settings, sites, codes & reporting",
        icon: hubFa(faLayerGroup),
        roles: [...ADMIN_ROLES],
    },
    {
        title: "Audit Logs",
        to: appRoutes.auditLogs,
        description: "View activity history",
        icon: hubFa(faClipboardList),
        roles: [...ADMIN_ROLES],
    },
];

/** Administration shortcuts on the Settings page (admin-only). */
export const settingsAdminHubCards: HubCardDef[] = [
    {
        title: SectionLabels.adminFoundation,
        to: appRoutes.systemSetup,
        description: "Admin entry points",
        icon: hubFa(faLayerGroup),
        roles: [...ADMIN_ROLES],
    },
    {
        title: "Users",
        to: appRoutes.users,
        description: "Manage users and roles",
        icon: hubFa(faUsers),
        roles: [...ADMIN_ROLES],
    },
    {
        title: "Company",
        to: appRoutes.company,
        description: "Company profile and details",
        icon: hubFa(faBuilding),
        roles: [...ADMIN_ROLES],
    },
    {
        title: "Website settings",
        to: appRoutes.websiteSettings,
        description: "Site configuration",
        icon: hubFa(faGlobe),
        roles: [...ADMIN_ROLES],
    },
    {
        title: "Organisations",
        to: appRoutes.organisations,
        description: "Organisation structure",
        icon: hubFa(faSitemap),
        roles: [...ADMIN_ROLES],
    },
    {
        title: "EA Waste Return Template",
        to: appRoutes.wasteReturnTemplate,
        description: "Upload EA workbook and configure export mapping",
        icon: hubFa(faClipboardList),
        roles: [...ADMIN_ROLES],
    },
];

export const operationsChildPaths = ["/operations", "/sites", "/customers", "/locations", appRoutes.wasteReceived, appRoutes.wasteRemoved, appRoutes.wasteReturns] as const;

export const movementPartiesChildPaths = [
    appRoutes.transportParties,
    "/vehicles",
    "/hauliers",
    "/tippers",
    "/processors",
] as const;

export const referenceDataChildPaths = [
    appRoutes.codes,
    appRoutes.wasteTypes,
    ...referenceAdminListPaths,
] as const;

/** Exact paths that must highlight the System sidebar parent (admin / configuration). */
const systemAdminExactPaths = new Set<string>([
    appRoutes.settings,
    appRoutes.users,
    appRoutes.organisations,
    appRoutes.company,
    appRoutes.websiteSettings,
    appRoutes.systemSetup,
    appRoutes.admin,
    appRoutes.invitations,
    appRoutes.userSessions,
    appRoutes.checklists,
    appRoutes.automaticEmails,
    appRoutes.automaticEmailsEdit,
    appRoutes.wasteReturnTemplate,
]);

const systemAdminPathPrefixes = [appRoutes.automaticEmails];

export function isSystemAdminPath(pathname: string): boolean {
    if (systemAdminExactPaths.has(pathname)) {
        return true;
    }
    return systemAdminPathPrefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

export function isSystemNavActive(pathname: string): boolean {
    if (pathname === appRoutes.system || pathname === appRoutes.auditLogs) {
        return true;
    }
    return isSystemAdminPath(pathname);
}

export function getSidebarActiveParentPath(pathname: string): string | null {
    if (pathname === "/dashboard") {
        return "/dashboard";
    }
    if (isSystemNavActive(pathname)) {
        return "/system";
    }
    if (operationsChildPaths.some((p) => pathname === p || pathname.startsWith(`${p}/`))) {
        return "/operations";
    }
    if (movementPartiesChildPaths.some((p) => pathname === p || pathname.startsWith(`${p}/`))) {
        return appRoutes.transportParties;
    }
    if (referenceDataChildPaths.some((p) => pathname === p || pathname.startsWith(`${p}/`))) {
        return appRoutes.codes;
    }
    return null;
}

export function isSidebarNavItemActive(itemPath: string, pathname: string): boolean {
    const active = getSidebarActiveParentPath(pathname);
    return active !== null && active === itemPath;
}
